import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAppSelector } from "./hooks/useAppStore";
import { selectAdminIsAuth } from "./store/slices/adminAuthSlice";
import { AdminLayout } from "./components/layout/AdminLayout";
import { RoleProtectedRoute } from "./components/layout/RoleProtectedRoute";

const LoginPage = lazy(() => import("./pages/Login"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const UsersPage = lazy(() => import("./pages/Users"));
const UserProfilePage = lazy(() => import("./pages/UserProfile"));
const VerificationsPage = lazy(() => import("./pages/Verifications"));
const PlansPage = lazy(() => import("./pages/Plans"));
const KingsQueensPage = lazy(() => import("./pages/KingsQueens"));
const AdminsPage = lazy(() => import("./pages/Admins"));
const ModerationPage = lazy(() => import("./pages/Moderation"));
const PaymentsPage = lazy(() => import("./pages/Payments"));
const SubscriptionsPage = lazy(() => import("./pages/Subscriptions"));
const MessagesPage = lazy(() => import("./pages/Messages"));
const GeneralSettingsPage = lazy(
  () => import("./pages/Settings/GeneralSettings"),
);
const MonetizationSettingsPage = lazy(
  () => import("./pages/Settings/MonetizationSettings"),
);
const AdminProfilePage = lazy(() => import("./pages/AdminProfile"));
const EventsPage = lazy(() => import("./pages/Events"));
const RosesPage = lazy(() => import("./pages/Roses"));
const NotificationsPage = lazy(() => import("./pages/Notifications"));
const SupportPage = lazy(() => import("./pages/Support"));
const MatchesPage = lazy(() => import("./pages/Matches"));
const LogsPage = lazy(() => import("./pages/Logs"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogs"));
const DeletedUsersPage = lazy(() => import("./pages/DeletedUsers"));
const StoriesPage = lazy(() => import("./pages/Stories"));
const ReportsPage = lazy(() => import("./pages/Reports"));
const BlockedAccountsPage = lazy(() => import("./pages/BlockedAccounts"));
const BlogPage = lazy(() => import("./pages/Blog"));
const SuccessStoriesPage = lazy(() => import("./pages/SuccessStories"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-charcoal">
      <div className="w-8 h-8 border-2 border-magenta border-t-transparent rounded-lg animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuth = useAppSelector(selectAdminIsAuth);
  if (!isAuth) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<RoleProtectedRoute><DashboardPage /></RoleProtectedRoute>} />
          <Route path="users" element={<RoleProtectedRoute><UsersPage /></RoleProtectedRoute>} />
          <Route path="users/:id" element={<RoleProtectedRoute><UserProfilePage /></RoleProtectedRoute>} />
          <Route path="verifications" element={<RoleProtectedRoute><VerificationsPage /></RoleProtectedRoute>} />
          <Route path="plans" element={<RoleProtectedRoute><PlansPage /></RoleProtectedRoute>} />
          <Route path="kings-and-queens" element={<RoleProtectedRoute><KingsQueensPage /></RoleProtectedRoute>} />
          <Route
            path="hotties"
            element={<Navigate to="/kings-and-queens" replace />}
          />
          <Route path="admins" element={<RoleProtectedRoute><AdminsPage /></RoleProtectedRoute>} />
          <Route path="moderation" element={<RoleProtectedRoute><ModerationPage /></RoleProtectedRoute>} />
          <Route path="payments" element={<RoleProtectedRoute><PaymentsPage /></RoleProtectedRoute>} />
          <Route path="subscriptions" element={<RoleProtectedRoute><SubscriptionsPage /></RoleProtectedRoute>} />
          <Route path="roses" element={<RoleProtectedRoute><RosesPage /></RoleProtectedRoute>} />
          <Route path="messages" element={<RoleProtectedRoute><MessagesPage /></RoleProtectedRoute>} />
          <Route
            path="settings"
            element={<Navigate to="/settings/general" replace />}
          />
          <Route path="settings/general" element={<RoleProtectedRoute><GeneralSettingsPage /></RoleProtectedRoute>} />
          <Route
            path="settings/monetization"
            element={<RoleProtectedRoute><MonetizationSettingsPage /></RoleProtectedRoute>}
          />
          <Route path="admin-profile" element={<RoleProtectedRoute><AdminProfilePage /></RoleProtectedRoute>} />
          <Route path="events" element={<RoleProtectedRoute><EventsPage /></RoleProtectedRoute>} />
          <Route path="notifications" element={<RoleProtectedRoute><NotificationsPage /></RoleProtectedRoute>} />
          <Route path="support" element={<RoleProtectedRoute><SupportPage /></RoleProtectedRoute>} />
          <Route path="matches" element={<RoleProtectedRoute><MatchesPage /></RoleProtectedRoute>} />
          <Route path="logs" element={<RoleProtectedRoute><LogsPage /></RoleProtectedRoute>} />
          <Route path="audit-logs" element={<RoleProtectedRoute><AuditLogsPage /></RoleProtectedRoute>} />
          <Route path="deleted-users" element={<RoleProtectedRoute><DeletedUsersPage /></RoleProtectedRoute>} />
          <Route path="stories" element={<RoleProtectedRoute><StoriesPage /></RoleProtectedRoute>} />
          <Route path="reports" element={<RoleProtectedRoute><ReportsPage /></RoleProtectedRoute>} />
          <Route path="blocked-accounts" element={<RoleProtectedRoute><BlockedAccountsPage /></RoleProtectedRoute>} />
          <Route path="blog" element={<RoleProtectedRoute><BlogPage /></RoleProtectedRoute>} />
          <Route path="success-stories" element={<RoleProtectedRoute><SuccessStoriesPage /></RoleProtectedRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
